const FACEBOOK_SCRIPT_ID = 'mall-facebook-pixel-script'
const TIKTOK_SCRIPT_ID = 'mall-tiktok-pixel-script'
const PURCHASE_DEDUP_STORAGE_KEY = 'mall_pixel_purchase_dedup'
const MAX_PENDING_EVENT_COUNT = 100
const MAX_PURCHASE_DEDUP_COUNT = 50

const initializedFacebookPixelIds = new Set()
const initializedTikTokPixelIds = new Set()
const activeFacebookPixelIds = new Set()
const activeTikTokPixelIds = new Set()
const pendingEvents = []

let lastPageViewKey = ''

const PIXEL_EVENT_MAP = {
  auth_register_success: { facebook: 'CompleteRegistration', tiktok: 'CompleteRegistration' },
  auth_login_success: { facebook: 'Contact', tiktok: 'Login' },
  tb_goods_detail_view: { facebook: 'ViewContent', tiktok: 'ViewContent' },
  alibaba_goods_detail_view: { facebook: 'ViewContent', tiktok: 'ViewContent' },
  tb_add_to_cart: { facebook: 'AddToCart', tiktok: 'AddToCart' },
  alibaba_add_to_cart: { facebook: 'AddToCart', tiktok: 'AddToCart' },
  tb_buy_now: { facebook: 'InitiateCheckout', tiktok: 'InitiateCheckout' },
  alibaba_buy_now: { facebook: 'InitiateCheckout', tiktok: 'InitiateCheckout' },
  tb_cart_checkout: { facebook: 'InitiateCheckout', tiktok: 'InitiateCheckout' },
  alibaba_cart_checkout: { facebook: 'InitiateCheckout', tiktok: 'InitiateCheckout' },
  tb_order_create_success: { tiktok: 'PlaceAnOrder' },
  alibaba_order_create_success: { tiktok: 'PlaceAnOrder' },
  tb_payment_start: { facebook: 'AddPaymentInfo', tiktok: 'AddPaymentInfo' },
  alibaba_payment_start: { facebook: 'AddPaymentInfo', tiktok: 'AddPaymentInfo' },
  tb_payment_success: { facebook: 'Purchase', tiktok: 'Purchase', requiresValidatedPurchase: true },
  alibaba_payment_success: { facebook: 'Purchase', tiktok: 'Purchase', requiresValidatedPurchase: true },
  expert_purchase_success: { facebook: 'Purchase', tiktok: 'Purchase' },
}

function getWindow() {
  return typeof window !== 'undefined' ? window : null
}

function parseJsonObject(value) {
  if (!value)
    return {}
  if (typeof value === 'object' && !Array.isArray(value))
    return value
  if (typeof value !== 'string')
    return {}

  try {
    const parsed = JSON.parse(value)
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {}
  }
  catch {
    return {}
  }
}

function normalizePixelIds(value) {
  if (!Array.isArray(value))
    return []
  return [...new Set(value
    .map(item => String(item ?? '').trim())
    .filter(Boolean))]
}

function resolveMallSiteConfig(configData) {
  const parsedConfig = parseJsonObject(configData)
  const directMallSiteConfig = parseJsonObject(parsedConfig.mallSiteConfig)
  if (Object.keys(directMallSiteConfig).length)
    return directMallSiteConfig

  const nestedOtherJson = parseJsonObject(parsedConfig.otherJson)
  const nestedMallSiteConfig = parseJsonObject(nestedOtherJson.mallSiteConfig)
  if (Object.keys(nestedMallSiteConfig).length)
    return nestedMallSiteConfig

  const followData = parseJsonObject(parsedConfig.offiaccountFollowData || parsedConfig.offiaccountFollowUrl)
  const followOtherJson = parseJsonObject(followData.otherJson)
  return parseJsonObject(followOtherJson.mallSiteConfig)
}

export function resolvePixelTrackingConfig(configData) {
  const mallSiteConfig = resolveMallSiteConfig(configData)
  return {
    facebookPixelIds: normalizePixelIds(mallSiteConfig.metaPixels),
    tiktokPixelIds: normalizePixelIds(mallSiteConfig.tiktokPixels),
  }
}

function insertExternalScript(win, id, src) {
  if (!win?.document || win.document.getElementById(id))
    return

  const script = win.document.createElement('script')
  script.id = id
  script.async = true
  script.src = src
  const firstScript = win.document.getElementsByTagName('script')[0]
  if (firstScript?.parentNode) {
    firstScript.parentNode.insertBefore(script, firstScript)
    return
  }
  win.document.head?.appendChild(script)
}

function ensureFacebookPixel(win) {
  if (typeof win.fbq !== 'function') {
    const fbq = function facebookPixelQueue() {
      if (fbq.callMethod) {
        // eslint-disable-next-line prefer-rest-params
        Reflect.apply(fbq.callMethod, fbq, arguments)
        return
      }
      // eslint-disable-next-line prefer-rest-params
      fbq.queue.push(arguments)
    }
    fbq.push = fbq
    fbq.loaded = true
    fbq.version = '2.0'
    fbq.queue = []
    win.fbq = fbq
    win._fbq = fbq
  }

  insertExternalScript(win, FACEBOOK_SCRIPT_ID, 'https://connect.facebook.net/en_US/fbevents.js')
}

function ensureTikTokPixel(win) {
  if (!win.ttq) {
    const ttq = []
    const methods = [
      'page',
      'track',
      'identify',
      'instances',
      'debug',
      'on',
      'off',
      'once',
      'ready',
      'alias',
      'group',
      'enableCookie',
      'disableCookie',
      'holdConsent',
      'revokeConsent',
      'grantConsent',
    ]

    ttq.methods = methods
    ttq.setAndDefer = (target, method) => {
      target[method] = (...args) => target.push([method, ...args])
    }
    methods.forEach(method => ttq.setAndDefer(ttq, method))
    ttq.instance = (pixelId) => {
      const instance = ttq._i?.[pixelId] || []
      methods.forEach(method => ttq.setAndDefer(instance, method))
      return instance
    }
    ttq.load = (pixelId, options = {}) => {
      const eventsUrl = 'https://analytics.tiktok.com/i18n/pixel/events.js'
      ttq._i = ttq._i || {}
      ttq._i[pixelId] = []
      ttq._i[pixelId]._u = eventsUrl
      ttq._t = ttq._t || {}
      ttq._t[pixelId] = Date.now()
      ttq._o = ttq._o || {}
      ttq._o[pixelId] = options
      insertExternalScript(
        win,
        `${TIKTOK_SCRIPT_ID}-${pixelId}`,
        `${eventsUrl}?sdkid=${encodeURIComponent(pixelId)}&lib=ttq`,
      )
    }

    win.TiktokAnalyticsObject = 'ttq'
    win.ttq = ttq
  }
}

function hasActivePixels() {
  return activeFacebookPixelIds.size > 0 || activeTikTokPixelIds.size > 0
}

export function hasActivePixelAnalytics() {
  return hasActivePixels()
}

function isPixelDebugEnabled() {
  const win = getWindow()
  return Boolean(import.meta.dev || import.meta.env?.DEV || win?.__MALL_PIXEL_DEBUG__ === true)
}

function logPixelDebugInitialization(status) {
  if (!isPixelDebugEnabled())
    return
  const win = getWindow()
  // eslint-disable-next-line no-console
  console.info('[Mall Pixel][dev:init]', {
    status,
    facebookPixelIds: [...activeFacebookPixelIds],
    tiktokPixelIds: [...activeTikTokPixelIds],
    facebookSdkAvailable: typeof win?.fbq === 'function',
    tiktokSdkAvailable: Boolean(win?.ttq),
  })
}

function logPixelDebugEvent(sourceEventName, mapping, params, status, preparedEvent) {
  if (!isPixelDebugEnabled())
    return
  const prepared = preparedEvent || prepareMappedEvent(mapping, params)
  // eslint-disable-next-line no-console
  console.info('[Mall Pixel][dev:event]', {
    status,
    sourceEventName,
    orderId: resolveOrderId(params) || undefined,
    eventId: prepared.eventId,
    facebook: mapping.facebook
      ? {
          pixelIds: [...activeFacebookPixelIds],
          eventName: mapping.facebook,
          payload: prepared.facebook,
        }
      : undefined,
    tiktok: mapping.tiktok
      ? {
          pixelIds: [...activeTikTokPixelIds],
          eventName: mapping.tiktok,
          payload: prepared.tiktok,
        }
      : undefined,
  })
}

function logPixelDebugPageView(status, pageViewKey) {
  if (!isPixelDebugEnabled())
    return
  // eslint-disable-next-line no-console
  console.info('[Mall Pixel][dev:page-view]', {
    status,
    pageViewKey,
    facebook: {
      pixelIds: [...activeFacebookPixelIds],
      eventName: 'PageView',
    },
    tiktok: {
      pixelIds: [...activeTikTokPixelIds],
      eventName: 'PageView',
    },
  })
}

function enqueuePendingEvent(event) {
  pendingEvents.push(event)
  if (pendingEvents.length > MAX_PENDING_EVENT_COUNT) {
    pendingEvents.splice(0, pendingEvents.length - MAX_PENDING_EVENT_COUNT)
  }
}

function flushPendingEvents() {
  if (!hasActivePixels() || !pendingEvents.length)
    return
  const events = pendingEvents.splice(0, pendingEvents.length)
  events.forEach((event) => {
    if (event.type === 'page_view') {
      const tracked = dispatchPageView(event.params)
      logPixelDebugPageView(
        tracked ? 'queued_event_dispatch_accepted' : 'queued_event_dispatch_failed',
        event.pageViewKey,
      )
    }
    else {
      const tracked = dispatchMappedEvent(event.mapping, event.params, event.preparedEvent)
      logPixelDebugEvent(
        event.sourceEventName,
        event.mapping,
        event.params,
        tracked ? 'queued_event_dispatch_accepted' : 'queued_event_dispatch_failed',
        event.preparedEvent,
      )
    }
  })
}

export function initializePixelAnalytics(configData) {
  const win = getWindow()
  if (!win?.document)
    return false

  const { facebookPixelIds, tiktokPixelIds } = resolvePixelTrackingConfig(configData)
  activeFacebookPixelIds.clear()
  activeTikTokPixelIds.clear()
  facebookPixelIds.forEach(id => activeFacebookPixelIds.add(id))
  tiktokPixelIds.forEach(id => activeTikTokPixelIds.add(id))

  if (facebookPixelIds.length) {
    try {
      ensureFacebookPixel(win)
      facebookPixelIds.forEach((pixelId) => {
        if (initializedFacebookPixelIds.has(pixelId))
          return
        win.fbq('init', pixelId)
        initializedFacebookPixelIds.add(pixelId)
      })
    }
    catch {
      // A blocked third-party SDK must not interrupt application initialization.
    }
  }

  if (tiktokPixelIds.length) {
    try {
      ensureTikTokPixel(win)
      tiktokPixelIds.forEach((pixelId) => {
        if (initializedTikTokPixelIds.has(pixelId))
          return
        win.ttq.load(pixelId)
        initializedTikTokPixelIds.add(pixelId)
      })
    }
    catch {
      // A blocked third-party SDK must not interrupt application initialization.
    }
  }

  flushPendingEvents()
  const initialized = hasActivePixels()
  logPixelDebugInitialization(initialized ? 'pixel_config_initialized' : 'pixel_config_missing_or_invalid')
  return initialized
}

function toFiniteNumber(value) {
  if (value === undefined || value === null || value === '')
    return null
  const numberValue = Number(value)
  return Number.isFinite(numberValue) ? numberValue : null
}

function toMajorCurrencyAmount(value, amountUnit = 'minor') {
  const numberValue = toFiniteNumber(value)
  if (numberValue === null)
    return 0
  return amountUnit === 'major' ? numberValue : numberValue / 100
}

function normalizeContentItem(item = {}, amountUnit = 'minor') {
  const source = item && typeof item === 'object' ? item : {}
  const itemId = source.content_id || source.itemId || source.item_id || source.offerId || source.productSpuId
  const skuId = source.itemSkuId || source.item_sku_id || source.skuId || source.sku_id || source.specId
  const contentId = String(itemId || skuId || '').trim()
  if (!contentId)
    return null

  const quantity = toFiniteNumber(source.quantity) || 1
  const price = toMajorCurrencyAmount(source.pixelPrice ?? source.price, source.amountUnit || amountUnit)
  const contentName = String(source.content_name || source.title || source.itemName || source.item_name || '').trim()

  return {
    content_id: contentId,
    content_name: contentName || undefined,
    content_type: 'product',
    price,
    quantity,
    sku_id: skuId ? String(skuId) : undefined,
  }
}

function normalizeContents(params = {}) {
  const amountUnit = params.amountUnit || 'minor'
  const sourceItems = Array.isArray(params.pixelContents)
    ? params.pixelContents
    : Array.isArray(params.items) && params.items.length
      ? params.items
      : [params]

  return sourceItems
    .map(item => normalizeContentItem(item, amountUnit))
    .filter(Boolean)
}

function resolveEventValue(params = {}, contents = []) {
  const explicitPixelValue = toFiniteNumber(params.pixelValue)
  if (explicitPixelValue !== null)
    return explicitPixelValue

  const rawTotal = params.totalPrice ?? params.total_price ?? params.value
  const totalValue = toFiniteNumber(rawTotal)
  if (totalValue !== null)
    return toMajorCurrencyAmount(totalValue, params.amountUnit || 'minor')

  return contents.reduce((total, item) => total + (item.price || 0) * (item.quantity || 1), 0)
}

function removeUndefinedValues(source) {
  return Object.entries(source).reduce((result, [key, value]) => {
    if (value !== undefined && value !== null && value !== '')
      result[key] = value
    return result
  }, {})
}

function resolveOrderId(params = {}) {
  const orderId = params.orderId
    || params.order_id
    || params.orderCode
    || params.order_code
    || params.purchaseId
    || params.purchase_id
    || (Array.isArray(params.orderIds) ? params.orderIds[0] : '')
  return String(orderId || '').trim()
}

function generateEventId(eventName, params = {}) {
  const orderId = resolveOrderId(params)
  if (eventName === 'Purchase' && orderId)
    return `purchase_${orderId}`
  return `${eventName}_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`
}

function readPurchaseDedupList() {
  const win = getWindow()
  if (!win?.localStorage)
    return []
  try {
    const parsed = JSON.parse(win.localStorage.getItem(PURCHASE_DEDUP_STORAGE_KEY) || '[]')
    return Array.isArray(parsed) ? parsed : []
  }
  catch {
    return []
  }
}

function hasSentPurchase(provider, orderId) {
  return Boolean(orderId) && readPurchaseDedupList().includes(`${provider}:${orderId}`)
}

function markPurchaseSent(provider, orderId) {
  const win = getWindow()
  if (!win?.localStorage || !orderId)
    return
  try {
    const key = `${provider}:${orderId}`
    const nextList = [key, ...readPurchaseDedupList().filter(item => item !== key)]
      .slice(0, MAX_PURCHASE_DEDUP_COUNT)
    win.localStorage.setItem(PURCHASE_DEDUP_STORAGE_KEY, JSON.stringify(nextList))
  }
  catch {
    // Pixel tracking is best-effort and must never interrupt the purchase flow.
  }
}

function buildPlatformPayloads(eventName, params = {}) {
  const eventId = String(params.eventId || params.event_id || generateEventId(eventName, params))
  const authMethod = String(params.method || '').trim()
  const trackingChannel = String(params.mall_tracking_channel || '').trim() || undefined
  if (['CompleteRegistration', 'Contact', 'Login'].includes(eventName)) {
    return {
      eventId,
      facebook: removeUndefinedValues(
        eventName === 'CompleteRegistration'
          ? {
              registration_method: authMethod || undefined,
              mall_tracking_channel: trackingChannel,
            }
          : {
              method: authMethod || undefined,
              mall_tracking_channel: trackingChannel,
            },
      ),
      tiktok: removeUndefinedValues({
        method: authMethod || undefined,
        mall_tracking_channel: trackingChannel,
      }),
    }
  }

  const contents = normalizeContents(params)
  const value = resolveEventValue(params, contents)
  const currency = String(params.currency || params.currencyCode || 'CNY').trim().toUpperCase() || 'CNY'
  const contentIds = contents.map(item => item.content_id)
  const itemCount = toFiniteNumber(params.itemCount)
    || contents.reduce((total, item) => total + (item.quantity || 1), 0)
  const facebook = removeUndefinedValues({
    content_ids: contentIds.length ? contentIds : undefined,
    content_name: contents[0]?.content_name,
    content_type: 'product',
    contents: contents.length
      ? contents.map(item => removeUndefinedValues({
          id: item.content_id,
          item_price: item.price,
          quantity: item.quantity,
        }))
      : undefined,
    currency,
    mall_tracking_channel: trackingChannel,
    num_items: itemCount || undefined,
    value,
  })

  const tiktok = removeUndefinedValues({
    contents: contents.length
      ? contents.map(item => removeUndefinedValues(item))
      : undefined,
    currency,
    mall_tracking_channel: trackingChannel,
    value,
  })

  return { eventId, facebook, tiktok }
}

function prepareMappedEvent(mapping, params = {}) {
  const eventName = mapping.facebook || mapping.tiktok
  return buildPlatformPayloads(eventName, params)
}

function dispatchFacebookEvent(eventName, payload, eventId, orderId) {
  const win = getWindow()
  if (!win?.fbq || !activeFacebookPixelIds.size)
    return false
  if (eventName === 'Purchase' && hasSentPurchase('facebook', orderId))
    return false

  try {
    activeFacebookPixelIds.forEach((pixelId) => {
      win.fbq('trackSingle', pixelId, eventName, payload, { eventID: eventId })
    })
    if (eventName === 'Purchase')
      markPurchaseSent('facebook', orderId)
    return true
  }
  catch {
    return false
  }
}

function dispatchTikTokEvent(eventName, payload, eventId, orderId) {
  const win = getWindow()
  if (!win?.ttq || !activeTikTokPixelIds.size)
    return false
  if (eventName === 'Purchase' && hasSentPurchase('tiktok', orderId))
    return false

  try {
    activeTikTokPixelIds.forEach((pixelId) => {
      win.ttq.instance(pixelId).track(eventName, payload, { event_id: eventId })
    })
    if (eventName === 'Purchase')
      markPurchaseSent('tiktok', orderId)
    return true
  }
  catch {
    return false
  }
}

function dispatchMappedEvent(mapping, params = {}, preparedEvent) {
  const { eventId, facebook, tiktok } = preparedEvent || prepareMappedEvent(mapping, params)
  const orderId = resolveOrderId(params)
  const facebookTracked = mapping.facebook
    ? dispatchFacebookEvent(mapping.facebook, facebook, eventId, orderId)
    : false
  const tiktokTracked = mapping.tiktok
    ? dispatchTikTokEvent(mapping.tiktok, tiktok, eventId, orderId)
    : false
  return facebookTracked || tiktokTracked
}

export function trackPixelAnalyticsEvent(eventName, params = {}) {
  const mapping = PIXEL_EVENT_MAP[eventName]
  if (!mapping || !getWindow())
    return false
  const preparedEvent = prepareMappedEvent(mapping, params)
  if (mapping.requiresValidatedPurchase) {
    logPixelDebugEvent(
      eventName,
      mapping,
      params,
      'purchase_waiting_for_order_validation',
      preparedEvent,
    )
    return false
  }

  if (!hasActivePixels()) {
    enqueuePendingEvent({
      type: 'event',
      sourceEventName: eventName,
      mapping,
      params: { ...params },
      preparedEvent,
    })
    logPixelDebugEvent(eventName, mapping, params, 'sdk_not_initialized_event_queued', preparedEvent)
    return true
  }
  try {
    const tracked = dispatchMappedEvent(mapping, params, preparedEvent)
    logPixelDebugEvent(
      eventName,
      mapping,
      params,
      tracked ? 'sdk_dispatch_accepted' : 'sdk_dispatch_failed',
      preparedEvent,
    )
    return tracked
  }
  catch {
    logPixelDebugEvent(eventName, mapping, params, 'sdk_dispatch_exception', preparedEvent)
    return false
  }
}

export function trackValidatedPixelPurchase(eventName, params = {}) {
  const mapping = PIXEL_EVENT_MAP[eventName]
  if (!mapping?.requiresValidatedPurchase || !getWindow())
    return false
  const preparedEvent = prepareMappedEvent(mapping, params)

  const orderId = resolveOrderId(params)
  if (!orderId) {
    logPixelDebugEvent(eventName, mapping, params, 'validated_purchase_missing_order_id', preparedEvent)
    return false
  }
  if (!hasActivePixels()) {
    logPixelDebugEvent(eventName, mapping, params, 'validated_purchase_sdk_not_initialized', preparedEvent)
    return false
  }

  try {
    const { eventId, facebook, tiktok } = preparedEvent
    const facebookComplete = !mapping.facebook
      || !activeFacebookPixelIds.size
      || hasSentPurchase('facebook', orderId)
      || dispatchFacebookEvent(mapping.facebook, facebook, eventId, orderId)
    const tiktokComplete = !mapping.tiktok
      || !activeTikTokPixelIds.size
      || hasSentPurchase('tiktok', orderId)
      || dispatchTikTokEvent(mapping.tiktok, tiktok, eventId, orderId)
    const tracked = facebookComplete && tiktokComplete
    logPixelDebugEvent(
      eventName,
      mapping,
      params,
      tracked ? 'validated_purchase_dispatch_complete' : 'validated_purchase_dispatch_failed',
      preparedEvent,
    )
    return tracked
  }
  catch {
    logPixelDebugEvent(eventName, mapping, params, 'validated_purchase_dispatch_exception', preparedEvent)
    return false
  }
}

function dispatchPageView(params = {}) {
  const win = getWindow()
  if (!win)
    return false

  const pageParams = removeUndefinedValues({
    page_location: win.location?.href,
    mall_tracking_channel: params.mall_tracking_channel,
  })
  let tracked = false
  if (typeof win.fbq === 'function') {
    activeFacebookPixelIds.forEach((pixelId) => {
      try {
        win.fbq('trackSingle', pixelId, 'PageView', pageParams)
        tracked = true
      }
      catch {
        // Continue with any other configured pixels.
      }
    })
  }
  if (win.ttq) {
    activeTikTokPixelIds.forEach((pixelId) => {
      try {
        win.ttq.instance(pixelId).page()
        tracked = true
      }
      catch {
        // Continue with any other configured pixels.
      }
    })
  }
  return tracked
}

export function trackPixelPageView(screenName, routeParams = {}) {
  if (!getWindow())
    return false
  const pageViewKey = String(routeParams.fullPath || routeParams.path || screenName || '').trim()
  if (!pageViewKey || pageViewKey === lastPageViewKey)
    return false
  lastPageViewKey = pageViewKey

  if (!hasActivePixels()) {
    enqueuePendingEvent({ type: 'page_view', pageViewKey, params: { ...routeParams } })
    logPixelDebugPageView('sdk_not_initialized_event_queued', pageViewKey)
    return true
  }
  const tracked = dispatchPageView(routeParams)
  logPixelDebugPageView(tracked ? 'sdk_dispatch_accepted' : 'sdk_dispatch_failed', pageViewKey)
  return tracked
}
